import { z } from "zod";

// Environment-dependent API base URL
const getApiBaseUrl = (): string => {
  // Use environment variable if available
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  if (typeof window !== "undefined") {
    // Browser environment
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      return "http://localhost:8787";
    }
    // Production environment - same origin (empty string means same host)
    return "";
  }
  // Server-side rendering or non-browser environment
  return "http://localhost:8787";
};

const API_BASE_URL = getApiBaseUrl();

// API Response types
interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  message?: string;
}

interface ApiErrorResponse {
  success: false;
  error: string;
  details?: any;
}

type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

// API Error class
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly details?: any;

  constructor(message: string, statusCode: number = 500, details?: any) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.details = details;
  }

  static fromResponse(
    errorResponse: ApiErrorResponse,
    statusCode: number = 500
  ): ApiError {
    return new ApiError(errorResponse.error, statusCode, errorResponse.details);
  }

  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }
}

// Generic HTTP client
class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  private async makeRequest<T = any>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    endpoint: string,
    options: {
      body?: any;
      headers?: Record<string, string>;
      params?: Record<string, string | number | boolean>;
      requiresAuth?: boolean;
      timeout?: number;
    } = {}
  ): Promise<T> {
    const {
      body,
      headers = {},
      params,
      requiresAuth = false,
      timeout = 30000, // 30 seconds
    } = options;

    // Build URL
    let url = `${this.baseURL}${endpoint}`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      url += `?${searchParams.toString()}`;
    }

    // Merge headers
    const requestHeaders = { ...this.defaultHeaders, ...headers };

    // Add authentication if required
    if (requiresAuth) {
      const token = getAuthToken();
      if (token) {
        requestHeaders.Authorization = `Bearer ${token}`;
      } else {
        throw new ApiError("Authentication required", 401);
      }
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle different response types
      if (!response.ok) {
        let errorData: ApiErrorResponse;

        try {
          errorData = await response.json();
        } catch {
          // Fallback for non-JSON error responses
          errorData = {
            success: false,
            error: response.statusText || `HTTP ${response.status}`,
          };
        }

        throw ApiError.fromResponse(errorData, response.status);
      }

      // Handle successful responses
      if (response.status === 204) {
        // No Content response
        return undefined as T;
      }

      try {
        const data = await response.json();

        // Check if it's our API response format
        if (typeof data === "object" && data !== null && "success" in data) {
          if (data.success) {
            return data.data as T;
          } else {
            throw ApiError.fromResponse(
              data as ApiErrorResponse,
              response.status
            );
          }
        }

        // Return raw data for non-API responses
        return data as T;
      } catch (parseError) {
        // Handle non-JSON responses
        if (response.headers.get("content-type")?.includes("text/")) {
          return (await response.text()) as unknown as T;
        }

        throw new ApiError("Invalid response format", response.status);
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error.name === "AbortError") {
        throw new ApiError("Request timeout", 408);
      }

      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new ApiError("Network connection failed", 0);
      }

      console.error("API Request failed:", error);
      throw new ApiError("An unexpected error occurred", 500, error);
    }
  }

  // HTTP method helpers
  async get<T = any>(
    endpoint: string,
    options?: Omit<Parameters<typeof this.makeRequest>[2], "body">
  ): Promise<T> {
    return this.makeRequest<T>("GET", endpoint, options);
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    options?: Omit<Parameters<typeof this.makeRequest>[2], "body">
  ): Promise<T> {
    return this.makeRequest<T>("POST", endpoint, { ...options, body });
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    options?: Omit<Parameters<typeof this.makeRequest>[2], "body">
  ): Promise<T> {
    return this.makeRequest<T>("PUT", endpoint, { ...options, body });
  }

  async delete<T = any>(
    endpoint: string,
    options?: Omit<Parameters<typeof this.makeRequest>[2], "body">
  ): Promise<T> {
    return this.makeRequest<T>("DELETE", endpoint, options);
  }

  // Add authorization header
  setAuthToken(token: string) {
    this.defaultHeaders.Authorization = `Bearer ${token}`;
  }

  // Remove authorization header
  clearAuthToken() {
    delete this.defaultHeaders.Authorization;
  }
}

// Create API client instance
const apiClient = new ApiClient(API_BASE_URL);

// Authentication helper functions
export function getAuthToken(): string | null {
  if (typeof window !== "undefined") {
    return localStorage.getItem("shopglobal_auth_token");
  }
  return null;
}

export function setAuthToken(token: string): void {
  if (typeof window !== "undefined") {
    localStorage.setItem("shopglobal_auth_token", token);
    apiClient.setAuthToken(token);
  }
}

export function clearAuthToken(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem("shopglobal_auth_token");
    localStorage.removeItem("shopglobal_user");
  }
  apiClient.clearAuthToken();
}

// Authentication API
export const authApi = {
  // Start signup process - send OTP (unified endpoint)
  async startSignup(
    email: string
  ): Promise<{ testOtp?: string; message: string; isNewUser?: boolean }> {
    return apiClient.post("/api/auth/otp/start", { email });
  },

  // Complete signup with OTP
  async completeSignup(
    email: string,
    otp: string,
    fullName: string,
    postalCode: string
  ): Promise<{
    user: {
      id: string;
      email: string;
      fullName: string;
      delawareAddress: string;
      membershipTier: string;
    };
    session: { token: string };
  }> {
    const response = await apiClient.post("/api/auth/signup/complete", {
      email,
      otp,
    });

    // Store auth data
    if (response.session?.token) {
      setAuthToken(response.session.token);
      if (typeof window !== "undefined") {
        localStorage.setItem("shopglobal_user", JSON.stringify(response.user));
      }
    }

    return response;
  },

  // Sign in
  async signin(
    email: string,
    password: string
  ): Promise<{
    user: any;
    session: { token: string };
  }> {
    const response = await apiClient.post("/api/auth/signin", {
      email,
      password,
    });

    // Store auth data
    if (response.session?.token) {
      setAuthToken(response.session.token);
      if (typeof window !== "undefined") {
        localStorage.setItem("shopglobal_user", JSON.stringify(response.user));
      }
    }

    return response;
  },

  // Verify session
  async verifySession(): Promise<any> {
    try {
      return await apiClient.get("/api/auth/verify", { requiresAuth: true });
    } catch (error: any) {
      // Only clear token if it's an auth error, not a network error
      if (
        error?.message?.includes("Invalid or expired session") ||
        error?.message?.includes("No token provided") ||
        error?.status === 401
      ) {
        clearAuthToken();
      }
      throw error;
    }
  },

  // Logout (client-side only)
  logout(): void {
    clearAuthToken();
  },
};

// Package API
export const packageApi = {
  // Get user's packages
  async getPackages(options?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{
    packages: any[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    // In a real app, you'd implement pagination
    return apiClient.get("/api/packages", {
      requiresAuth: true,
      params: options,
    });
  },

  // Get single package
  async getPackage(packageId: string): Promise<any> {
    return apiClient.get(`/api/packages/${packageId}`, {
      requiresAuth: true,
    });
  },

  // Track package by tracking number (public)
  async trackPackage(
    trackingNumber: string
  ): Promise<{ package: any; tracking: any[] }> {
    return apiClient.get(`/api/track/${trackingNumber}`);
  },
};

// Payment API
export const paymentApi = {
  // Create payment checkout session for package
  async createPackageCheckout(
    packageId: string,
    options?: {
      insurance?: boolean;
      customs_declaration?: boolean;
    }
  ): Promise<{ sessionId: string; paymentUrl: string; total: number }> {
    return apiClient.post(
      `/api/payments/packages/${packageId}/checkout`,
      options || {},
      { requiresAuth: true }
    );
  },

  // Create consolidation payment
  async createConsolidationCheckout(
    groupId: string,
    options?: {
      insurance?: boolean;
      customs_declaration?: boolean;
    }
  ): Promise<{
    sessionId: string;
    paymentUrl: string;
    total: number;
    savings: number;
  }> {
    return apiClient.post(
      `/api/payments/consolidation/${groupId}/checkout`,
      options || {},
      { requiresAuth: true }
    );
  },

  // Get payment history
  async getPaymentHistory(): Promise<any[]> {
    return apiClient.get("/api/payments/history", { requiresAuth: true });
  },
};

// Shipping calculator API
export const shippingApi = {
  // Calculate shipping rates
  async calculateShipping(options: {
    weight: number;
    dimensions: { length: number; width: number; height: number };
    destinationCountry?: string;
  }): Promise<
    Array<{
      carrier: string;
      service: string;
      cost: number;
      currency: string;
      estimatedDays: { min: number; max: number };
    }>
  > {
    return apiClient.post("/api/shipping/calculate", options);
  },

  // Get shipping quote (authenticated)
  async getShippingQuote(packageDetails: any): Promise<any> {
    return apiClient.post("/api/shipping/quote", packageDetails, {
      requiresAuth: true,
    });
  },
};

// Health check
export const healthApi = {
  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    return apiClient.get("/health");
  },
};

// Type definitions for API responses
export interface User {
  id: string;
  email: string;
  fullName: string;
  delawareAddress: string;
  membershipTier: "free" | "premium" | "business";
  canadianAddress?: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
}

export interface Package {
  id: string;
  user_id: string;
  tracking_number?: string;
  sender_name?: string;
  weight: number;
  dimensions: string; // JSON string
  declared_value?: number;
  status:
    | "incoming"
    | "received"
    | "paid"
    | "shipped"
    | "delivered"
    | "delayed"
    | "damaged";
  expected?: boolean;
  warehouse_id: string;
  photos?: string[]; // JSON array of URLs
  received_at?: string;
  shipped_at?: string;
  delivered_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Warehouse {
  id: string;
  name: string;
  address_line_1: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  tax_free: boolean;
  region: string;
}

// Helper function to get current user
export function getCurrentUser(): User | null {
  if (typeof window === "undefined") return null;

  try {
    const userJson = localStorage.getItem("shopglobal_user");
    return userJson ? JSON.parse(userJson) : null;
  } catch (error) {
    console.error("Failed to parse user data:", error);
    clearAuthToken();
    return null;
  }
}

// Auth state helper
export function isAuthenticated(): boolean {
  return !!getAuthToken() && !!getCurrentUser();
}

// Export default API client for advanced usage
export { apiClient as ApiClient };
export default apiClient;
